/*!
 * Product classification / PVO detection (KeyName ProductClassification, legacy keys supported).
 */

const getPrimitiveTextValue = (value: unknown): string => {
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return value.toString();
    }
    return '';
};

/** Normalized attribute name/key for comparisons (e.g. "Product Classification" → productclassification). */
export const normalizePhibroAttributeKey = (name?: unknown): string =>
    getPrimitiveTextValue(name)
        .replace(/[^a-z0-9]/gi, '')
        .toLocaleLowerCase();

const isProductClassificationNormalizedKey = (normalized: string): boolean =>
    normalized === 'productclassification' || normalized === 'vsiproductclassification' || normalized === 'vsiclassification';

/**
 * True when Name or KeyName identifies the commerce product classification attribute
 * (current: KeyName ProductClassification / Name "Product Classification"; legacy keys included).
 */
export const isProductClassificationAttribute = (attribute: { Name?: unknown; KeyName?: unknown } | undefined | null): boolean => {
    if (!attribute) {
        return false;
    }
    const n = normalizePhibroAttributeKey(attribute.Name);
    const k = normalizePhibroAttributeKey(attribute.KeyName);
    return isProductClassificationNormalizedKey(n) || isProductClassificationNormalizedKey(k);
};

/** Scalar display value from a commerce attribute (TextValue, typed fields, extension properties). */
export const getCommerceAttributeScalarValue = (attribute: any): string => {
    const textValue = getPrimitiveTextValue(attribute?.TextValue);
    if (textValue) {
        return textValue;
    }

    if (attribute?.BooleanValue !== undefined && attribute?.BooleanValue !== null) {
        return attribute.BooleanValue ? 'Yes' : 'No';
    }

    if (attribute?.IntegerValue !== undefined && attribute?.IntegerValue !== null) {
        return attribute.IntegerValue.toString();
    }

    if (attribute?.FloatValue !== undefined && attribute?.FloatValue !== null) {
        return attribute.FloatValue.toString();
    }

    if (attribute?.CurrencyValue !== undefined && attribute?.CurrencyValue !== null) {
        return attribute.CurrencyValue.toString();
    }

    const extensionValue = attribute?.ExtensionProperties?.find((property: any) => !!property?.Value)?.Value;
    const extensionStringValue = getPrimitiveTextValue(extensionValue?.StringValue);
    if (extensionStringValue) {
        return extensionStringValue;
    }

    if (extensionValue?.BooleanValue !== undefined && extensionValue?.BooleanValue !== null) {
        return extensionValue.BooleanValue ? 'Yes' : 'No';
    }

    if (extensionValue?.DecimalValue !== undefined && extensionValue?.DecimalValue !== null) {
        return extensionValue.DecimalValue.toString();
    }

    if (extensionValue?.IntegerValue !== undefined && extensionValue?.IntegerValue !== null) {
        return extensionValue.IntegerValue.toString();
    }

    if (extensionValue?.LongValue !== undefined && extensionValue?.LongValue !== null) {
        return extensionValue.LongValue.toString();
    }

    return '';
};

export const findProductClassificationAttribute = (attributes: any[] | undefined | null): any | undefined => {
    if (!attributes?.length) {
        return undefined;
    }
    const list = attributes.slice?.() ?? attributes;
    return list.find((a: any) => isProductClassificationAttribute(a));
};

/** Raw classification string from AttributeValues (e.g. PVO, ISPVO), or null if missing. */
export const getProductClassificationDisplayValue = (attributes: any[] | undefined | null): string | null => {
    const attr = findProductClassificationAttribute(attributes);
    if (!attr) {
        return null;
    }
    const v = getCommerceAttributeScalarValue(attr);
    return v ? v.trim() : null;
};

/**
 * PVO inventory / UI rules: treat value as PVO when it normalizes to pvo / ispvo / contains pvo
 * (aligned with PLP / PDP browse behavior).
 */
export const isPvoProductClassification = (value: string | null | undefined): boolean => {
    const normalized = (value ?? '').replace(/[^a-z0-9]/gi, '').toLocaleLowerCase();
    return normalized === 'pvo' || normalized === 'ispvo' || normalized.includes('pvo');
};
